/**
 * Агроцикл как строка сервера (ядро S4): чтение и запись `/agrocycles`.
 *
 * Свой модуль, а не строки в общем `api`: путь и формы ответа принадлежат
 * экрану агроцикла. Клиент ничего не считает — план, участники, «Скоро» и
 * KPI приходят готовыми (слой метрик), здесь только вызовы.
 */

import { request, type QueryParams, type Schemas } from '../../../api/client';

export type AgrocycleRow = Schemas['AgrocycleOut'];
export type AgrocycleDetail = Schemas['AgrocycleDetailOut'];
export type AgrocycleList = Schemas['AgrocyclesOut'];
export type PlanWork = Schemas['CyclePlanWorkOut'];
export type PlanStage = Schemas['CyclePlanStageOut'];
export type StartCheck = Schemas['CycleStartCheckOut'];
export type EditResult = Schemas['CycleEditOut'];
export type Kpi = Schemas['CycleKpiOut'];
export type Source = Schemas['CycleSourceOut'];
export type StageProgress = Schemas['CycleStageProgressOut'];

const path = (key: string) => `/agrocycles/${encodeURIComponent(key)}`;
const json = (method: string, body?: unknown): RequestInit => ({
  method,
  body: body === undefined ? undefined : JSON.stringify(body),
});

export const agrocycleApi = {
  list: (params: QueryParams) => request<AgrocycleList>('/agrocycles', params),
  /** «Скоро» хозяйства или района: работы плана без кампании в горизонте (S5). */
  upcoming: (params: QueryParams) => request<Schemas['UpcomingOut']>('/agrocycles/upcoming', params),
  detail: (key: string, params: QueryParams) => request<AgrocycleDetail>(path(key), params),
  create: (body: { crop_code: string; planting_year: number }, params: QueryParams) =>
    request<AgrocycleDetail>('/agrocycles', params, json('POST', body)),
  startCheck: (key: string, params: QueryParams) =>
    request<StartCheck>(`${path(key)}/start-check`, params),
  start: (key: string, token: string, params: QueryParams) =>
    request<AgrocycleDetail>(`${path(key)}/start`, params, json('POST', { token })),
  finish: (key: string, params: QueryParams) =>
    request<Schemas['CycleFinishOut']>(`${path(key)}/finish`, params, json('POST')),
  addWork: (key: string, body: Schemas['CycleWorkIn'], params: QueryParams) =>
    request<EditResult>(`${path(key)}/works`, params, json('POST', body)),
  patchWork: (key: string, id: number, body: Schemas['CycleWorkPatchIn'], params: QueryParams) =>
    request<EditResult>(`${path(key)}/works/${id}`, params, json('PATCH', body)),
  removeWork: (key: string, id: number, reason: string, params: QueryParams) =>
    request<EditResult>(`${path(key)}/works/${id}`, { ...params, reason }, json('DELETE')),
};
